import {
	HttpClient,
	HttpErrorResponse,
	HttpHeaders,
} from '@angular/common/http';
// Angular modules.
import { EventEmitter, Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/auth';
import { TranslateService } from '@ngx-translate/core';
import { ApiErrorResponse } from 'app/interfaces/error.interface';
// Zenklub interfaces.
import {
	IBillingFile,
	IProfessionalPaymentAccount,
	IProfessionalPerson,
	IProfessionalProfile,
	IProfessionalProfileUpdate,
	IZkuProfessional,
	ProfileCompletionResponse,
	ProfileSessionPriceUpdate,
	ProfileSubscriptionAndContactTiers,
	RequestActivationResponse,
	UploadPhotoResponse,
	UploadSignatureResponse,
} from 'app/interfaces/professionalprofile.interface';
import {
	IPreviousSpeedTest,
	ISpeedTestUpdateData,
} from 'app/interfaces/user.interface';
import { IUserPreferences } from 'app/interfaces/userpreferences.interface';
import firebase from 'firebase';
// import { DateTimeAdapter } from 'ng-pick-datetime';
import { Observable, Subscriber, of, throwError } from 'rxjs';
import { catchError, concatMap } from 'rxjs/operators';

// Zenklub services.
import { GlobalService } from './global.service';
import { ZkuService } from './zku.service';

interface IAccountLink {
	url: string;
	accountId: string;
}
@Injectable()
export class UserService {
	// Cache service
	collectionName = 'user';
	private firebaseUser: firebase.User;
	profileUpdated = new EventEmitter();
	public defaultUserTimezone = 'America/Sao_Paulo';
	// Constructor.
	constructor(
		private global: GlobalService,
		private translate: TranslateService,
		// private dateTimeAdapter: DateTimeAdapter<any>,
		private httpApi: HttpClient,
		private http: HttpClient,
		private zkuService: ZkuService,
		private firebaseAuth: AngularFireAuth
	) {}

	// Verify if user is logged in.
	public async isLoggedIn(): Promise<boolean> {
		return (await this.getUserId()) ? true : false;
	}

	// Get user ID.
	public getUserId() {
		return new Promise<string>(async (resolve) => {
			const user = await this.getFirebaseUser();
			resolve(user?.uid);
		});
	}

	// get firebase user
	public getFirebaseUser() {
		return new Promise<firebase.User>((resolve) => {
			if (this.firebaseUser) {
				resolve(this.firebaseUser);
			} else {
				this.firebaseAuth.user.subscribe((user) => {
					this.firebaseUser = user;
					resolve(user);
				});
			}
		});
	}

	public getAuthState() {
		return this.firebaseAuth.authState;
	}

	public async getToken() {
		return (await this.getFirebaseUser()).getIdToken();
	}

	public getUserLocal() {
		return this.zkuService.getZku();
	}

	public getTimezone(): string {
		const user = this.zkuService.getZku();

		return user?.timezone || this.defaultUserTimezone;
	}

	// Get user's profile data.
	public getProfessionalProfileData(
		id?: string
	): Observable<IProfessionalProfile> {
		return new Observable((async (
			subscriber: Subscriber<IProfessionalProfile>
		) => {
			// Ensure ID.
			if (!id) {
				await this.getFirebaseUser();
			}
			// Get data.
			this.httpApi
				.get<IProfessionalProfile>('professionals/profile')
				.subscribe((response: IProfessionalProfile) =>
					subscriber.next(response)
				);
		}) as any);
	}

	public checkSubscriptionAndContactTiers(
		id?: string
	): Promise<ProfileSubscriptionAndContactTiers> {
		return new Promise(async (resolve) => {
			if (!id) {
				await this.getFirebaseUser();
			}

			this.httpApi
				.get<ProfileSubscriptionAndContactTiers>(
					'professionals/profile/checkSubscriptionAndContactTiers'
				)
				.subscribe((response: ProfileSubscriptionAndContactTiers) => {
					resolve(response);
				});
		});
	}

	public checkSessionPriceUpdate(
		id?: string
	): Promise<ProfileSessionPriceUpdate> {
		return new Promise(async (resolve) => {
			if (!id) {
				await this.getFirebaseUser();
			}

			this.httpApi
				.get<ProfileSessionPriceUpdate>(
					'professionals/profile/checkSessionPriceUpdate'
				)
				.subscribe((response: ProfileSessionPriceUpdate) => {
					resolve(response);
				});
		});
	}

	// Set online status.
	public setOnlineStatus(isOnline: boolean) {
		return new Observable((async (observer) => {
			// Ger user ID.
			const userId: string = await this.getUserId();
			if (userId) {
				// Set status string;
				const strOnline: string = isOnline ? 'true' : 'false';
				const url = `professionals/${userId}/isvisible/${strOnline}`;
				return this.httpApi.put(url, null).subscribe(
					() => {
						localStorage.setItem('zkisvisible', strOnline);
						observer.next();
					},
					() => {
						observer.next();
					}
				);
			} else {
				observer.next();
			}
		}) as any);
	}

	// Update user's profile.
	public updateProfileData(
		upData: Partial<IProfessionalProfileUpdate>,
		path = ''
	): Observable<undefined> {
		this.replaceEmptyForNull(upData);
		return this.httpApi.put<undefined>(`professionals/profile${path}`, upData);
	}

	public updatePaymentData(
		upData: Partial<IProfessionalPaymentAccount>
	): Observable<undefined> {
		this.replaceEmptyForNull(upData);
		return this.httpApi.put<undefined>(
			`professionals/profile/financial/payment`,
			upData
		);
	}

	private replaceEmptyForNull(upData) {
		Object.keys(upData).forEach((k) => {
			if (!upData[k] && typeof upData[k] !== 'boolean') {
				upData[k] = null;
			}
		});
	}

	// Delete user's video
	public deleteProfileVideo(): Observable<undefined> {
		return this.httpApi.delete<undefined>('professionals/profile/video');
	}

	// Update user's photo.
	public updatePhoto(imageFile: File): Observable<string> {
		// Endpoint address.
		let imageUrl: string;
		const url = 'professionals/profile/photo';

		return this.http
			.put<UploadPhotoResponse>(url, { fileMime: imageFile.type }, {})
			.pipe(
				concatMap((result) => {
					imageUrl = result.imageUrl;
					const uploadHeaders = new HttpHeaders();
					uploadHeaders.append('Content-Type', imageFile.type);

					return this.http.put(result.uploadUrl, imageFile, {
						headers: uploadHeaders,
					});
				}),
				concatMap((response: any) => {
					if (response == null || (response && response.ok)) {
						return of(imageUrl);
					} else {
						throw new Error('Error uploading profile image');
					}
				}),
				catchError((err: HttpErrorResponse) => {
					return throwError(new ApiErrorResponse(err));
				})
			);
	}

	public uploadSignature(file: File): Observable<string> {
		let signatureUrl: string;
		const url = 'professionals/profile/signature';

		return this.http
			.put<UploadSignatureResponse>(url, { fileMime: file.type }, {})
			.pipe(
				concatMap((result) => {
					signatureUrl = result.signatureUrl;
					const uploadHeaders = new HttpHeaders();
					uploadHeaders.append('Content-Type', file.type);

					return this.http.put(result.uploadUrl, file, {
						headers: uploadHeaders,
					});
				}),
				concatMap((response: any) => {
					if (response == null || (response && response.ok)) {
						return of(signatureUrl);
					} else {
						throw new Error('Error uploading signature');
					}
				}),
				catchError((err: HttpErrorResponse) => {
					return throwError(new ApiErrorResponse(err));
				})
			);
	}

	public uploadFinancialFiles(billingFile: IBillingFile) {
		const url = 'professionals/profile/financial/upload';
		return this.http.put<{ urls: string[] }>(url, billingFile, {});
	}

	// Save user's data.
	public saveData(userData: IZkuProfessional) {
		const zku = this.getUserLocal() || ({} as IZkuProfessional);
		zku.imageUrl = userData.imageUrl || userData.image;
		zku.timezone = userData.timezone;
		zku.priceOneHour = userData.priceOneHour;
		zku.priceHalfHour = userData.priceHalfHour;
		zku.trainee = userData.trainee;
		zku.active = userData.active;
		zku.tags = userData?.tags;
		this.zkuService.setZku(zku);
	}

	// Get user's picture.
	public getUserPicture(id: string): Observable<string> {
		return new Observable<string>((observer) => {
			this.getProfessionalProfileData(id).subscribe((result) => {
				observer.next(result.imageUrl || this.global.getDefaultUserPicture());
			});
		});
	}

	// Get stored user's picture.
	public getStoredUserPicture(): string {
		const zku = this.getUserLocal();
		return zku && zku.imageUrl
			? zku.imageUrl
			: this.global.getDefaultUserPicture();
	}

	// Get user's country.
	public getUserCountry(): string {
		const zku = this.getUserLocal();
		return zku && zku.country ? zku.country : 'br';
	}

	// Get user's profile data.
	public getProfileData(): Promise<IProfessionalProfile> {
		return new Promise(async (resolve) => {
			// Get user's ID.
			const userId: string = await this.getUserId();
			// Get data.
			this.getProfessionalProfileData(userId).subscribe((response) => {
				return resolve(response);
			});
		});
	}

	// Get user online status.
	public getOnlineStatus(): boolean {
		const zkisvisible: string = localStorage.getItem('zkisvisible');
		return zkisvisible && zkisvisible.toLowerCase() === 'true';
	}

	// Get local preferences.
	public getUserLocalPreferences(): IUserPreferences {
		const result = this.getUserLocal();
		const userPrefs: IUserPreferences = {
			Currency: 'USD',
			CurrencySymbol: '$',
			DateMask: 'YYYY/MM/DD',
			DatePlaceholder: 'yyyy/mm/dd',
			DecimalSeparator: '.',
			Language: 'en-US',
			SessionValue: result ? result.priceOneHour : null,
		};
		// Get user's country.
		const country: string = result
			? result.country
				? result.country
				: 'BR'
			: 'BR';
		switch (country.toUpperCase()) {
			case 'US':
				userPrefs.Currency = 'USD';
				userPrefs.CurrencySymbol = '$';
				userPrefs.DateMask = 'YYYY/MM/DD';
				userPrefs.DatePlaceholder = 'yyyy/mm/dd';
				userPrefs.DecimalSeparator = '.';
				userPrefs.Language = 'en-US';
				break;
			case 'PT':
				userPrefs.Currency = 'EUR';
				userPrefs.CurrencySymbol = '€';
				userPrefs.DateMask = 'DD/MM/YYYY';
				userPrefs.DatePlaceholder = 'dd/mm/yyyy';
				userPrefs.DecimalSeparator = ',';
				userPrefs.Language = 'pt-PT';
				break;
			case 'BR':
				userPrefs.Currency = 'BRL';
				userPrefs.CurrencySymbol = 'R$';
				userPrefs.DateMask = 'DD/MM/YYYY';
				userPrefs.DatePlaceholder = 'dd/mm/yyyy';
				userPrefs.DecimalSeparator = ',';
				userPrefs.Language = 'pt-BR';
				break;
			default:
				userPrefs.Currency = 'USD';
				userPrefs.CurrencySymbol = '$';
				userPrefs.DateMask = 'YYYY/MM/DD';
				userPrefs.DatePlaceholder = 'yyyy/mm/dd';
				userPrefs.DecimalSeparator = ',';
				userPrefs.Language = 'en-US';
				break;
		}
		// Update language.
		this.translate.use(userPrefs.Language);
		// this.dateTimeAdapter.setLocale(userPrefs.Language);
		return userPrefs;
	}

	public getSpeedTestData(): Observable<IPreviousSpeedTest> {
		return this.httpApi.get<any>(`professionals/speedtest/status`);
	}

	public updateSpeedTestData(
		speedTestData: ISpeedTestUpdateData
	): Observable<IPreviousSpeedTest> {
		return this.httpApi.post<any>(`professionals/speedtest`, speedTestData);
	}

	public requestActivation() {
		return this.httpApi.post<RequestActivationResponse>(
			'professionals/profile/activate',
			{}
		);
	}

	public requestCompletionStatus() {
		return this.httpApi.post<ProfileCompletionResponse>(
			'professionals/profile/completion',
			{}
		);
	}

	public linkAccount(refreshUrl: string, returnUrl: string) {
		return this.httpApi.post<IAccountLink>(
			`professionals/profile/linkaccount`,
			{ returnUrl, refreshUrl }
		);
	}

	public checkProfessionalPerson() {
		return this.httpApi.get<IProfessionalPerson>(
			'professionals/profile/checkProfessionalPerson'
		);
	}

	public cancelProfessionalPerson() {
		return this.httpApi.post<any>(
			'professionals/profile/cancelProfessionalPerson',
			{}
		);
	}

	public getSummaryCorporate() {
		return this.httpApi.get<
			{
				tier: string;
				duration: number;
				totalActives: number;
				totalEligibles: number;
			}[]
		>('professionals/profile/summary/corporate');
	}
}
